export function delphiTColorToHex(value: unknown, fallback: string = '#FFFFFF'): string {
  if (value === null || value === undefined) {
    return fallback;
  }

  const raw = String(value).trim();
  if (!raw) {
    return fallback;
  }

  let numericValue: number | null = null;

  if (/^-?\d+$/.test(raw)) {
    numericValue = parseInt(raw, 10);
  } else if (/^#[0-9a-fA-F]{6}$/.test(raw)) {
    return raw.toUpperCase();
  } else if (/^#[0-9a-fA-F]{8}$/.test(raw)) {
    return `#${raw.slice(3)}`.toUpperCase();
  } else if (/^0x[0-9a-fA-F]{6}$/.test(raw)) {
    return `#${raw.slice(2)}`.toUpperCase();
  } else if (/^0x[0-9a-fA-F]{8}$/.test(raw)) {
    numericValue = parseInt(raw, 16);
  }

  if (numericValue === null || Number.isNaN(numericValue)) {
    return fallback;
  }

  if (numericValue === 0 || numericValue === 0xffffff) {
    return '#FFFFFF';
  }

  const unsigned24 = numericValue < 0 ? (numericValue >>> 0) & 0xffffff : numericValue & 0xffffff;
  const r = unsigned24 & 0xff;
  const g = (unsigned24 >> 8) & 0xff;
  const b = (unsigned24 >> 16) & 0xff;

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

/**
 * Convert Delphi TColor integer (0x00BBGGRR) to CSS hex (#RRGGBB).
 * Examples:
 * - 255      -> #FF0000 (red)
 * - 65280    -> #00FF00 (green)
 * - 16711680 -> #0000FF (blue)
 */
export function delphiColorIntToCssHex(value: unknown, fallback: string = '#808080'): string {
  return delphiTColorToHex(value, fallback);
}

export function isDarkHexColor(hex: string): boolean {
  if (!/^#[0-9A-F]{6}$/i.test(hex)) {
    return false;
  }

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.35;
}

/**
 * Patient List Account # column / Patient Edit Colors: `commentColorCode` is packed **RRGGBB** (high byte = red).
 * Not Delphi BGR — use this instead of {@link delphiTColorToHex} for comment-category swatches.
 */
export function accountHighlightFromCommentColorCode(
  commentColorCode: unknown
): { backgroundColor: string; color: string } | null {
  if (commentColorCode == null || commentColorCode === '') {
    return null;
  }
  const codeNum =
    typeof commentColorCode === 'number' ? commentColorCode : parseInt(String(commentColorCode), 10);
  if (!Number.isFinite(codeNum) || Number.isNaN(codeNum)) {
    return null;
  }

  const r = (codeNum >> 16) & 0xff;
  const g = (codeNum >> 8) & 0xff;
  const b = codeNum & 0xff;
  const backgroundColor = `rgb(${r},${g},${b})`;

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const color = luminance > 0.5 ? '#1e293b' : '#fff';

  return { backgroundColor, color };
}

/** Visit Details / All Visits Account # column — Delphi colAccountNoCustomDrawCell (TPD.PatientColor). */
export function accountNoCellStyleFromDelphiPatientColor(
  row: Record<string, unknown> | null | undefined,
  column: { key?: string }
): { [key: string]: string } | undefined {
  if (column.key !== 'accountNo') {
    return undefined;
  }
  const raw = row?.['patientColor'];
  if (raw == null || raw === '') {
    return undefined;
  }
  const num = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (!Number.isFinite(num) || num <= 0) {
    return undefined;
  }
  const hex = delphiTColorToHex(num, '#FFFFFF');
  if (!hex || hex === '#FFFFFF') {
    return undefined;
  }
  const style: Record<string, string> = { backgroundColor: hex };
  if (isDarkHexColor(hex)) {
    style['color'] = '#ffffff';
  }
  return style;
}
